import type { HtmlTransform } from '../formatMessage'

/**
 * Post-markdown transform: wrap <h2> headings and their following
 * content in <details open><summary> elements.
 *
 * Only targets <h2> — <h1> is rare in chat, <h3>+ are subsections
 * within a collapsible block. Content before the first <h2> is
 * left unwrapped.
 *
 * Only kicks in for genuinely long, multi-section answers (>= MIN_SECTIONS
 * <h2> headings). One or two headings render as plain headings — wrapping
 * them in disclosure boxes just adds visual noise to short replies.
 *
 * Emitted structure (markdown-renderer.css styles each part):
 *   <details class="marcus-collapsible" open>
 *     <summary>…<h2>…</h2></summary>
 *     <div class="marcus-collapsible-body"> …section content… </div>
 *   </details>
 * The body wrapper is intentional: it gives the CSS one padded container per
 * section instead of padding every direct child, so spacing/alignment stay
 * stable regardless of what blocks the section contains.
 *
 * Streaming-safe: sections use `open` attribute so content is visible
 * immediately. Re-wraps correctly on each render as v-html recomputes.
 */
const MIN_SECTIONS = 3

export const collapsibleSections: HtmlTransform = (html) => {
  if (!html.includes('<h2')) return html

  const headingCount = (html.match(/<h2[^>]*>/g) || []).length
  if (headingCount < MIN_SECTIONS) return html

  const parts = html.split(/(<h2[^>]*>[\s\S]*?<\/h2>)/)
  if (parts.length <= 1) return html

  let result = ''
  let inDetails = false

  for (const part of parts) {
    if (part.match(/^<h2[^>]*>/)) {
      if (inDetails) result += '</div></details>'
      // Ensure a block break before the collapsible if preceded by inline text
      if (result.length > 0 && !result.trimEnd().endsWith('>')) {
        result += '\n'
      }
      result += `<details class="marcus-collapsible" open><summary>${part}</summary><div class="marcus-collapsible-body">`
      inDetails = true
    } else {
      result += part
    }
  }

  if (inDetails) result += '</div></details>'
  return result
}
